import { useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { useCollectionsStore } from '../store/collectionsStore';
import { useVariablesStore } from '../store/variablesStore';
import { useWorkspaceStore } from '../store/workspaceStore';
import { isLocalWorkspace } from '../utils/workspaceStorage';
import { apiClientKey } from '../services/storageNamespace';
import { persistHistory } from '../services/historyPersistence';

/**
 * Mirrors store state into localStorage caches. These are pure browser-side
 * writes (no Tauri / no service round-trip) extracted verbatim from App so the
 * shell stays thin. Cloud-only caches early-return for local workspaces, which
 * persist through the per-workspace snapshot instead.
 */
export function useLocalStoragePersistence() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const collections = useCollectionsStore((s) => s.collections);
  const history = useCollectionsStore((s) => s.history);
  const historyNs = useCollectionsStore((s) => s.historyNs);
  const environments = useVariablesStore((s) => s.environments);
  const selectedEnvironment = useVariablesStore((s) => s.selectedEnvironment);
  const globalVariables = useVariablesStore((s) => s.globalVariables);
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);

  // Save collections to localStorage whenever it changes (cloud workspaces only — cache)
  useEffect(() => {
    if (!currentWorkspace || isLocalWorkspace(currentWorkspace, isAuthenticated)) return;
    localStorage.setItem(apiClientKey('collections'), JSON.stringify(collections));
  }, [collections, currentWorkspace, isAuthenticated]);

  // Save history whenever it changes — only once it was loaded for the current
  // namespace (a first-render `[]` used to overwrite the saved list).
  useEffect(() => {
    persistHistory(history, historyNs);
  }, [history, historyNs]);

  // Save environments / selection / globals only for non-local mode (local uses snapshot effect)
  useEffect(() => {
    if (!currentWorkspace || isLocalWorkspace(currentWorkspace, isAuthenticated)) return;
    localStorage.setItem(apiClientKey('environments'), JSON.stringify(environments));
  }, [environments, currentWorkspace, isAuthenticated]);

  // Save selected environment to localStorage
  useEffect(() => {
    if (!currentWorkspace || isLocalWorkspace(currentWorkspace, isAuthenticated)) return;
    localStorage.setItem(apiClientKey('selected-environment'), selectedEnvironment?.id || '');
  }, [selectedEnvironment, currentWorkspace, isAuthenticated]);

  // Save global variables to localStorage
  useEffect(() => {
    if (!currentWorkspace || isLocalWorkspace(currentWorkspace, isAuthenticated)) return;
    localStorage.setItem(apiClientKey('global-variables'), JSON.stringify(globalVariables));
  }, [globalVariables, currentWorkspace, isAuthenticated]);

  // Save workspaces to localStorage
  useEffect(() => {
    localStorage.setItem(apiClientKey('workspaces'), JSON.stringify(workspaces));
  }, [workspaces]);

  // Save current workspace to localStorage
  useEffect(() => {
    if (currentWorkspace) {
      localStorage.setItem(apiClientKey('current-workspace'), currentWorkspace.id);
    }
  }, [currentWorkspace]);
}
